'use client';

import { cn } from '../../../../lib/utils';
import { ArrowDropDown } from '@crabs-ai/web-icons';
import { useEffect, useRef, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import { InspectorControlsStyles as S } from '../inspector-controls-styles';

export interface ColorInputFieldProps {
  /** 현재 색상 값 (HEX 코드) */
  value?: string;
  /** 레이블 표시 여부 */
  isShowLabel?: boolean;
  /** 레이블 텍스트 */
  label?: string;
  /** 접근성 라벨 */
  accessibilityLabel?: string;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** Mixed value 상태 (여러 아이템 선택 시) */
  hasMixedValue?: boolean;
  /** Mixed value일 때 표시할 라벨 */
  mixedLabel?: string;
  /** 드롭다운 커스텀 컨텐츠 (ColorPicker 컴포넌트) */
  dropdownContent?: React.ReactNode;
  /** 컬러 입력 필드 타입 */
  type?: 'default' | 'none';
  /** none 타입일 때 표시 텍스트 */
  noneLabel?: string;
  /** 드롭다운 너비 (기본값: '360px') */
  dropdownWidth?: string | number;
  /** 컴포넌트 래퍼에 추가할 클래스명 */
  className?: string;
  /** Popover 열림/닫힘 변경 핸들러 */
  onOpenChange?: (open: boolean) => void;
  /** Trigger 영역 마우스다운 핸들러 */
  onTriggerMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  /** Dropdown content focus outside 핸들러 */
  onDropdownFocusOutside?: React.ComponentProps<
    typeof PopoverContent
  >['onFocusOutside'];
  /** Dropdown content 내부 클릭으로 인한 close 요청 무시 여부 */
  keepOpenOnContentPointerDown?: boolean;
}

export default function ColorInputField({
  value = '#000000',
  isShowLabel = false,
  label,
  accessibilityLabel,
  disabled,
  hasMixedValue = false,
  mixedLabel = 'Mixed',
  dropdownContent,
  type = 'default',
  noneLabel = 'None',
  dropdownWidth = '360px',
  className,
  onOpenChange,
  onTriggerMouseDown,
  onDropdownFocusOutside,
  keepOpenOnContentPointerDown = false,
}: ColorInputFieldProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const lastPointerDownTargetRef = useRef<EventTarget | null>(null);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      lastPointerDownTargetRef.current = e.target;
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
      if (!el) return null;
      let current: HTMLElement | null = el.parentElement;
      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        if (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          current.scrollHeight > current.clientHeight
        ) {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    const scrollParent = getScrollParent(triggerRef.current);
    if (!scrollParent) return;

    const handleScroll = () => {
      setIsDropdownOpen(false);
      onOpenChange?.(false);
    };

    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollParent.removeEventListener('scroll', handleScroll);
    };
  }, [isDropdownOpen, onOpenChange]);

  const isNoneType = type === 'none' && !hasMixedValue;
  const displayValue = hasMixedValue
    ? mixedLabel
    : isNoneType
      ? noneLabel
      : value;
  const isSelected = isDropdownOpen;

  return (
    <div
      data-selected={isSelected}
      data-custom-state={disabled ? 'disabled' : 'default'}
      className={cn('group relative flex flex-col gap-5xs', className)}
    >
      {isShowLabel && label && (
        <div className={S.label.wrapper}>
          <label
            htmlFor={accessibilityLabel}
            className={cn(
              S.label.text,
              'group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light',
            )}
          >
            {label}
          </label>
        </div>
      )}
      <div className="relative flex w-full items-center">
        <Popover
          open={isDropdownOpen}
          onOpenChange={(open) => {
            const lastPointerTarget =
              lastPointerDownTargetRef.current as Node | null;
            const isPointerInsideContent =
              keepOpenOnContentPointerDown &&
              !open &&
              !!lastPointerTarget &&
              !!dropdownContentRef.current?.contains(lastPointerTarget);

            if (isPointerInsideContent) {
              return;
            }

            setIsDropdownOpen(open);
            onOpenChange?.(open);
          }}
        >
          <PopoverTrigger asChild>
            <div
              ref={triggerRef}
              onMouseDown={onTriggerMouseDown}
              className={cn(
                S.container,
                'flex-1',
                !disabled && 'hover:bg-comp-color-input-bg-hover',
                !disabled && 'active:bg-comp-color-input-bg-active',
                'group-data-[selected=true]:border group-data-[selected=true]:border-comp-color-input-border-selected group-data-[selected=true]:bg-comp-color-input-bg-selected',
                'transition-colors duration-200 ease-out',
                disabled &&
                  'cursor-not-allowed opacity-50 bg-comp-color-input-bg-disabled',
              )}
            >
              {isNoneType ? (
                <div
                  className={cn(
                    S.leadingNode.colorChip,
                    'relative overflow-hidden bg-white',
                  )}
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute inset-0 size-full"
                    aria-hidden="true"
                  >
                    <line
                      x1="2"
                      y1="18"
                      x2="18"
                      y2="2"
                      stroke="#F04C4C"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              ) : (
                <div
                  className={cn(
                    S.leadingNode.colorChip,
                    'group-data-[custom-state=disabled]:opacity-50',
                  )}
                  style={{
                    backgroundColor: hasMixedValue
                      ? 'transparent'
                      : value || '#000000',
                  }}
                />
              )}
              <span
                className={cn(
                  S.value.base,
                  'min-w-0',
                  S.value.filled,
                  'group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light',
                )}
              >
                {displayValue}
              </span>
              <div
                className={cn(
                  'flex items-center justify-center',
                  'cursor-pointer transition-all',
                  'flex-shrink-0 self-stretch px-2xs',
                  disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <ArrowDropDown
                  className={cn(
                    S.chevron.base,
                    isDropdownOpen && S.chevron.open,
                    'group-data-[custom-state=disabled]:[&_path]:fill-sys-cont-neutral-disabled-light',
                  )}
                />
              </div>
            </div>
          </PopoverTrigger>
          <PopoverContent
            side="left"
            sideOffset={8}
            collisionPadding={16}
            align="start"
            className="w-[360px] rounded-sm border border-sys-style-neutral-default bg-sys-bg-surface-1 p-sm inset-shadow-basic-md data-[state=open]:duration-200 data-[state=open]:ease-out data-[state=closed]:duration-150 data-[state=closed]:ease-in"
            style={{ width: dropdownWidth }}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onFocusOutside={onDropdownFocusOutside}
          >
            <div ref={dropdownContentRef}>{dropdownContent}</div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
